/**
 * The Brain workspace: one graph, its tabs, everything read from the API.
 *
 * Which graph is in the path, and the tab is in the query, so every view here
 * is a link. Choosing a graph happens on the index at `/brain`; this screen
 * always has one.
 */

import { useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ErrorNotice, Loading } from '@/shared/components'
import { AskDrawer } from './ask/AskDrawer'
import { EdgesTab } from './edges/EdgesTab'
import { GraphTab } from './graph/GraphTab'
import { NodesTab } from './nodes/NodesTab'
import { ObjectsTab } from './objects/ObjectsTab'
import { RecordsTab } from './records/RecordsTab'
import { SourcesTab } from './sources/SourcesTab'
import { BrainTopBar } from './shell/BrainTopBar'
import { useGraphModel, useGraphs, useGraphStats } from './hooks'
import './styles/brain.css'

const TABS = [
  { key: 'graph', label: 'Graph' },
  { key: 'nodes', label: 'Nodes' },
  { key: 'edges', label: 'Edges' },
  { key: 'objects', label: 'Objects' },
  { key: 'sources', label: 'Sources' },
  { key: 'records', label: 'Records' },
] as const

export type BrainTab = (typeof TABS)[number]['key']

export function BrainPage() {
  const { graphId: routeGraphId } = useParams<{ graphId?: string }>()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  const graphs = useGraphs()
  const graphId = routeGraphId ?? graphs.data?.[0]?.graph_id
  const model = useGraphModel(graphId)
  const stats = useGraphStats(graphId)

  const requestedTab = params.get('tab')
  const tab: BrainTab = TABS.find((item) => item.key === requestedTab)?.key ?? 'graph'

  const counts = useMemo(
    () => ({
      graph: undefined,
      nodes: model.data?.node_types.length,
      edges: model.data?.edge_types.length,
      objects: model.data?.node_types.length,
      sources: undefined,
      records: stats.data?.records,
    }),
    [model.data, stats.data],
  )

  function setTab(next: BrainTab) {
    const updated = new URLSearchParams(params)
    updated.set('tab', next)
    setParams(updated, { replace: true })
  }

  // A graph that does not exist — deleted, or a stale link — belongs on the
  // index, not on an error page.
  const missing = Boolean(
    graphs.data && (!graphs.data.length || (routeGraphId && !graphs.data.some((g) => g.graph_id === routeGraphId))),
  )
  useEffect(() => {
    if (missing) navigate('/brain', { replace: true })
  }, [missing, navigate])

  if (graphs.isLoading) return <Loading label="Opening the workspace…" />
  if (graphs.error) {
    return (
      <div className="page">
        <ErrorNotice error={graphs.error} onRetry={graphs.refetch} />
      </div>
    )
  }
  if (missing || !graphId || !graphs.data) return null

  return (
    <div className="brain">
      <div className="brain-workspace">
        <BrainTopBar
          graphs={graphs.data}
          graphId={graphId}
          onSwitch={(id) => navigate(`/brain/${id}?tab=${tab}`)}
        />

        <div className="cp-tabbar" role="tablist" aria-label="Graph views"
          onKeyDown={(event) => {
            const index = TABS.findIndex((item) => item.key === tab)
            const next = event.key === 'ArrowRight' ? (index + 1) % TABS.length
              : event.key === 'ArrowLeft' ? (index + TABS.length - 1) % TABS.length
              : event.key === 'Home' ? 0 : event.key === 'End' ? TABS.length - 1 : -1
            if (next < 0) return
            event.preventDefault()
            setTab(TABS[next].key)
            event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
          }}>

          {TABS.map((t) => (
            <button
              key={t.key}
              className="cp-tab"
              role="tab"
              tabIndex={t.key === tab ? 0 : -1}
              aria-selected={t.key === tab}
              data-on={t.key === tab}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {counts[t.key] !== undefined && (
                <span className="n">{counts[t.key]?.toLocaleString()}</span>
              )}
            </button>
          ))}
        </div>

        {model.isLoading && <Loading label="Loading the model…" />}
        {model.error && (
          <div style={{ padding: 16 }}>
            <ErrorNotice error={model.error} onRetry={model.refetch} />
          </div>
        )}

        {model.data && (
          <>
            {tab === 'graph' && <GraphTab graphId={graphId} model={model.data} />}
            {tab === 'nodes' && <NodesTab graphId={graphId} model={model.data} />}
            {tab === 'edges' && <EdgesTab graphId={graphId} model={model.data} />}
            {tab === 'objects' && <ObjectsTab graphId={graphId} model={model.data} />}
            {tab === 'sources' && <SourcesTab graphId={graphId} model={model.data} />}
            {tab === 'records' && <RecordsTab graphId={graphId} model={model.data} />}
          </>
        )}
      </div>

      <AskDrawer graphId={graphId} />
    </div>
  )
}
